import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { projects, filterOptions, getThumbFallbacks } from '../data/projects';
import ProjectModal from './ProjectModal';
import { ImageWithFallback } from './ImageWithFallback';

const Projetos = () => {
  const [activeFilter, setActiveFilter] = useState('todos');
  const [selectedProject, setSelectedProject] = useState(null);

  const filteredProjects = projects.filter((p) => {
    if (activeFilter === 'todos') return true;
    return p.categoria === activeFilter;
  });

  const openProject = (project) => {
    setSelectedProject(project);
  };

  const closeProject = () => {
    setSelectedProject(null);
  };

  const getCategoryLabel = (categoria) =>
    categoria === 'sites' ? 'Site' : 'Sistema';

  return (
    <section id="projetos" className="py-32 bg-white dark:bg-dark-surface transition-colors duration-500">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 gsap-reveal">
          <p className="text-burnt font-heading font-semibold uppercase tracking-wider text-sm mb-4">
            Portfólio
          </p>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-dark dark:text-cream mb-4">
            Projetos que falam por si
          </h2>
          <p className="text-xl text-dark/70 dark:text-cream/70 max-w-2xl mx-auto font-body">
            Modelos reais de sites e sistemas que desenvolvo.
          </p>
        </div>

        <div className="flex justify-center gap-3 mb-12 flex-wrap">
          {filterOptions.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                activeFilter === filter.id
                  ? 'bg-burnt text-white shadow-premium'
                  : 'bg-cream dark:bg-dark-bg text-dark dark:text-cream border border-brown/10 dark:border-burnt/20 hover:border-burnt/40'
              }`}
              data-cursor="pointer"
            >
              {filter.label}
            </button>
          ))}
        </div>

        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
          <AnimatePresence mode="popLayout">
            {filteredProjects.map((projeto) => (
              <motion.div
                key={projeto.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
                className="group cursor-pointer h-full"
                onClick={() => openProject(projeto)}
                data-cursor="pointer"
              >
                <div className="relative h-full flex flex-col rounded-2xl overflow-hidden bg-cream dark:bg-dark-bg border border-brown/10 dark:border-burnt/10 shadow-card dark:shadow-card-dark transition-all duration-500 hover:shadow-premium dark:hover:shadow-premium-dark hover:-translate-y-2 hover:border-burnt/30">
                  <div className="relative aspect-[16/10] flex-shrink-0 overflow-hidden bg-cream dark:bg-dark-surface flex items-center justify-center p-3">
                    <ImageWithFallback
                      src={projeto.thumb}
                      fallbackSources={getThumbFallbacks(projeto)}
                      alt={projeto.titulo}
                      className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-700"
                      placeholderLabel={projeto.titulo}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-dark/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-burnt/90 text-white backdrop-blur-sm">
                        {getCategoryLabel(projeto.categoria)}
                      </span>
                    </div>
                  </div>

                  <div className="p-6 flex flex-col flex-1 min-h-[11.5rem]">
                    <p className="text-xs font-semibold uppercase tracking-wider text-burnt mb-1 line-clamp-1 min-h-[1rem]">
                      {projeto.segmento}
                    </p>
                    <h3 className="text-xl font-heading font-semibold text-dark dark:text-cream mb-2 group-hover:text-burnt transition-colors line-clamp-2 min-h-[3.25rem]">
                      {projeto.titulo}
                    </h3>
                    <p className="text-sm text-dark/70 dark:text-cream/70 font-body line-clamp-2 min-h-[2.5rem] mb-4 flex-1">
                      {projeto.descricaoCurta}
                    </p>
                    <span className="inline-flex items-center gap-2 text-burnt text-sm font-semibold group-hover:gap-3 transition-all mt-auto">
                      Ver projeto <FaExternalLinkAlt className="text-xs" />
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {selectedProject && (
        <ProjectModal project={selectedProject} onClose={closeProject} />
      )}
    </section>
  );
};

export default Projetos;
